import { GRID_SETTINGS_CUSTOM_SORT_ID } from '../constants';
import type { GridSettingsConfig, GridSettingsPreference } from '../types';

function isStringArray(value: unknown): value is string[] {
    return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseGroupValueOrders(value: unknown): Record<string, string[]> | undefined {
    if (!isRecord(value)) {
        return undefined;
    }

    const result: Record<string, string[]> = {};
    for (const [field, order] of Object.entries(value)) {
        if (isStringArray(order)) {
            result[field] = order;
        }
    }

    return Object.keys(result).length > 0 ? result : undefined;
}

/** Validates the unknown payload stored in user preferences. */
export function parseGridSettingsPreference(value: unknown): GridSettingsPreference | null {
    if (!isRecord(value)) {
        return null;
    }

    const result: GridSettingsPreference = {};

    if (typeof value.sortBy === 'string' && value.sortBy.length > 0) {
        result.sortBy = value.sortBy;
    }

    if (isStringArray(value.groupFields)) {
        result.groupFields = value.groupFields;
    }

    const groupValueOrders = parseGroupValueOrders(value.groupValueOrders);
    if (groupValueOrders) {
        result.groupValueOrders = groupValueOrders;
    }

    if (typeof value.inGroupSortBy === 'string' && value.inGroupSortBy.length > 0) {
        result.inGroupSortBy = value.inGroupSortBy;
    }

    if (isStringArray(value.groupedFields)) {
        result.groupedFields = value.groupedFields;
    }

    if (!result.sortBy && !result.groupFields && !result.inGroupSortBy) {
        return null;
    }

    return result;
}

/** For search grids: turns a primary option into the `field_ASC` string the query already uses. */
export function preferenceToSortByQuery(
    preference: GridSettingsPreference | null,
    config?: GridSettingsConfig
): string | undefined {
    if (!preference?.sortBy || preference.sortBy === GRID_SETTINGS_CUSTOM_SORT_ID) {
        return undefined;
    }

    const option = config?.sortOptions.find((sortOption) => sortOption.id === preference.sortBy);
    if (!option?.field) {
        return undefined;
    }

    return `${option.field}_${(option.direction ?? 'asc').toUpperCase()}`;
}
